import { NextSeo } from 'next-seo'
import { useRouter } from 'next/router'
import React, { useEffect } from 'react'
import absoluteUrl from 'next-absolute-url'
import dynamic from 'next/dynamic'
import { Amplitude, LogOnMount } from '@amplitude/react-amplitude'

const Review = dynamic(() => import('../../src/components/Review/Token'))
import { TokenWithMarketData } from '../../types/token'
import { GetServerSideProps } from 'next'
import { LoadState } from '../../types/general'
import { getTokenWithMarketData } from '../../src/services/token'
import Nav from '../../src/components/Nav'
import Footer from '../../src/components/Footer'

interface Props {
  token?: TokenWithMarketData
}

function TokenPage({ token }: Props): JSX.Element {
  const { pathname } = useRouter()
  const url = `/token/${token?.path_id}`
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])

  if (!token) {
    return <div>Not Found</div>
  }

  return (
    <Amplitude
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      eventProperties={(inheritedProps: { page: any }) => ({
        ...inheritedProps,
        page: {
          ...inheritedProps.page,
          name: url,
        },
      })}
    >
      {(): JSX.Element => (
        <>
          <LogOnMount eventType="page-displayed" />
          <NextSeo
            title={token?.seo_title}
            description={token?.seo_description}
            openGraph={{
              url: `https://cryptotesters.com${url}`,
              title: token?.seo_title,
              description: token?.seo_description,
              images: token?.sharing_image && [
                {
                  url: token?.sharing_image.url,
                  width: 1200,
                  height: 630,
                  alt: `${token?.title} image - Cryptotesters.com`,
                },
              ],
              site_name: 'Cryptotesters.com',
            }}
            twitter={{
              handle: '@cryptotesters',
              site: '@cryptotesters',
              cardType: 'summary_large_image',
            }}
          />
          <Nav />
          <Review {...token} />
          <Footer />
        </>
      )}
    </Amplitude>
  )
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { origin } = absoluteUrl(context.req)
  const code = context.params?.code
  let token
  let loadState = LoadState.IDLE

  if (code) {
    try {
      token = await getTokenWithMarketData(origin, code)
      loadState = LoadState.LOADED
    } catch (e) {
      loadState = LoadState.ERROR
    }
  }

  return {
    props: {
      ...(token ? { token } : {}),
      initialLoadState: loadState,
    },
  }
}

export default TokenPage
